import React, { useState, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import RemoteLayout from "./layout";
import { Conference, UserIcon } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { ImageCloudV2, MultiSelectionPicker, SpeakingOrder } from "../../components/workshop";
import { defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute, sortListByLocale } from "../../util/general.util";

const TASK_ID = 7;

const Task7 = ({ pdf = false }) => {
  const { t, i18n } = useTranslation();

  const collageRef = useRef(null);

  const [taskOrderUsers, setTaskOrderUsers] = useState([]);
  const [images, setImages] = useState([]);
  const [isStarted, setIsStarted] = useState(false);
  const [userSelections, setUserSelections] = useState([]);

  const ProfessionalRoles = t("resources.roles", { returnObjects: true });

  const {
    socket,
    user: { id, role } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  useEffect(() => {
    socket.emit("getTaskActivityStatus", TASK_ID, (myTaskStatus) => {
      console.log("myTaskStatus", myTaskStatus);
      if (!myTaskStatus) return;
      setIsStarted(myTaskStatus?.ready);
    });

    socket?.on("taskActivityStatus", (taskActivityStatus) => {
      console.log("taskActivityStatus", taskActivityStatus);
      const myTaskStatus = taskActivityStatus[TASK_ID];
      if (!myTaskStatus) return;
      if (myTaskStatus?.ready) setIsStarted(true);
    });

    socket?.emit("taskOrder", TASK_ID, (users) => {
      if (users) setTaskOrderUsers(users);
    });

    socket.on("roles", (roles) => {
      if (!Object.keys(roles)?.length) return;

      setCollageHeight();

      renderImageCloud(roles);
    });

    socket.on("selectedRoles", (selectedRoles) => {
      setUserSelections(selectedRoles);
    });

    socket.emit("roles", id);

    window.addEventListener("resize", setCollageHeight);

    return () => {
      window.removeEventListener("resize", setCollageHeight);
    };
  }, [id]);

  const handleNext = () => {
    if (!isStarted) {
      socket.emit("taskActivityStatus", {
        taskId: TASK_ID,
        status: { ready: true }
      });
      setIsStarted(true);
      return;
    }
    const nextRoute = getNextRoute("/task-7", platform);
    socket.emit("navigate", nextRoute);
  };

  const renderImageCloud = (roles) => {
    const sortedProfessionalRoles = sortListByLocale(ProfessionalRoles, i18n.language, "title");

    const imageScores = [];

    Object.keys(roles).forEach((id) => {
      const targetImage = sortedProfessionalRoles?.[id]?.image;
      imageScores.push({
        url: targetImage,
        score: roles[id]
        // url: `${imageURLBase}/${i18n.language}/${i18n.language}_${Number(id) + 1}.${imageExtension}`,
      });
    });

    setImages(imageScores);
  };

  const setCollageHeight = () => {
    try {
      if (!collageRef.current?.clientWidth) return;

      const currentWidth = collageRef?.current?.clientWidth;
      collageRef.current.style.height = `${currentWidth * 0.7}px`;
    } catch (error) {
      console.error("Unable to set collage height", error);
    }
  };

  const handleButtonClick = () => {
    if (role !== defaultRoles.participant) {
      return handleNext;
    } else return null;
  };

  const buttonText =
    role !== defaultRoles.participant && isStarted
      ? t("task7.buttonNext") + " →"
      : role !== defaultRoles.participant && !isStarted
        ? t("task7.start") + " →"
        : null;

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-7`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton = role !== defaultRoles.participant && !isStarted;

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={handleButtonClick()}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {isStarted ? t("task7.started.title") : t("task7.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl text-lg mx-auto text-white-100"
          dangerouslySetInnerHTML={{
            __html: isStarted ? t("task7.started.description") : t("task7.description")
          }}
        />
      </div>

      {/* Main Content */}
      {isStarted ? (
        <>
          <div className="mx-auto flex items-center justify-center mb-6">
            <SpeakingOrder participants={taskOrderUsers} filterOnlineParticipants={true} />
          </div>
          <div
            className={`flex flex-col md:flex-row md:items-center gap-4 w-full ${role === defaultRoles.moderator && "justify-center"}`}>
            {role !== defaultRoles.moderator && (
              <div className="w-3/12 hidden md:block">
                <div className="w-full">
                  <div className="mt-4">
                    <MultiSelectionPicker
                      socket={socket}
                      moderator={role === defaultRoles.moderator}
                      userId={id}
                      updateId={"updateRoles"}
                      submitAllTogether={true}
                      disableSelection={userSelections?.length > 0}
                      dummySelections={userSelections || []}
                      title={t("mtParticipantActivityScreen.dropdownTwo")}
                      options={ProfessionalRoles?.map((r) => r?.title)}
                      maxOptions={2}
                      saveWithIndex={true}
                      enableSearchOption={true}
                      customClasses={"!bg-orange-100 !rounded-full !border-orange-100"}
                      textClasses={"text-white-100"}
                      selectionTitle={t("task7.selections.title")}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="w-full md:w-9/12">
              {role !== defaultRoles.moderator && (
                <div className="my-4 md:hidden">
                  <MultiSelectionPicker
                    socket={socket}
                    moderator={role === defaultRoles.moderator}
                    userId={id}
                    updateId={"updateRoles"}
                    submitAllTogether={true}
                    disableSelection={userSelections?.length > 0}
                    dummySelections={userSelections || []}
                    title={t("mtParticipantActivityScreen.dropdownTwo")}
                    options={ProfessionalRoles?.map((r) => r?.title)}
                    maxOptions={2}
                    saveWithIndex={true}
                    enableSearchOption={true}
                    customClasses={"!bg-orange-100 !rounded-full !border-orange-100"}
                    textClasses={"text-white-100"}
                    selectionTitle={t("task7.selections.title")}
                  />
                </div>
              )}
              <div className=" bg-white-100 rounded-xl ">
                {images?.length > 0 ? (
                  <div ref={collageRef} className="w-full mx-auto h-[50vh]">
                    <ImageCloudV2 images={images} pdf={pdf} />
                  </div>
                ) : (
                  <div className="w-full md:w-[500px] mx-auto h-[50vh] flex items-center justify-center">
                    <p className="text-black-200 text-center text-xl font-bold">
                      {t("task7.noImages")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-y-auto mt-4 w-full">
            <Conference isLinear={true} />
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 w-full">
          {/* Task Overview Card */}
          <div className="bg-orange-50 text-black p-6 rounded-2xl shadow-2xl col-span-1 w-full h-fit">
            <h2 className="text-orange-default font-bold text-xl mb-4">{t("task7.card.title")}</h2>
            <ul
              className="list-inside"
              dangerouslySetInnerHTML={{ __html: t("task7.card.list.item1") }}
            />
          </div>
          <div className="col-span-1 md:col-span-2 w-full">
            {/* Participants Grid */}
            <Conference smallSpace={true} />
          </div>
        </div>
      )}
    </RemoteLayout>
  );
};

export default Task7;
